import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/config/supabase'
import { Clock, CheckCircle, StickyNote, Tag, Calendar, UserPlus } from 'lucide-react'
import type { LeadActivity } from '@/types'

interface Props {
  leadId: string
}

const ACTION_CONFIG: Record<string, { icon: typeof Clock; label: string; color: string }> = {
  created: { icon: UserPlus, label: 'Lead creato', color: 'text-blue-500 bg-blue-100' },
  status_changed: { icon: CheckCircle, label: 'Stato cambiato', color: 'text-green-500 bg-green-100' },
  note_added: { icon: StickyNote, label: 'Nota aggiunta', color: 'text-purple-500 bg-purple-100' },
  callback_set: { icon: Calendar, label: 'Richiamo programmato', color: 'text-orange-500 bg-orange-100' },
  callback_removed: { icon: Calendar, label: 'Richiamo rimosso', color: 'text-gray-500 bg-gray-100' },
  tag_added: { icon: Tag, label: 'Tag aggiunto', color: 'text-indigo-500 bg-indigo-100' },
  tag_removed: { icon: Tag, label: 'Tag rimosso', color: 'text-gray-500 bg-gray-100' },
}

const STATUS_LABELS: Record<string, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  callback: 'Da richiamare',
  acquired: 'Acquisito',
}

export function LeadTimeline({ leadId }: Props) {
  const [activities, setActivities] = useState<LeadActivity[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadActivities = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('lead_activity_log_cagliari')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (!error && data) {
        setActivities(data as LeadActivity[])
      }
    } catch {
      // silently fail
    } finally {
      setIsLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })

  const renderDetails = (activity: LeadActivity) => {
    const d = activity.details || {}
    switch (activity.action) {
      case 'status_changed':
        return (
          <span>
            {STATUS_LABELS[d.from] || d.from} → <strong>{STATUS_LABELS[d.to] || d.to}</strong>
          </span>
        )
      case 'callback_set':
        return <span>Per il {d.date ? new Date(d.date).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '?'}</span>
      case 'tag_added':
      case 'tag_removed':
        return <span className="font-medium">{d.tag}</span>
      case 'note_added':
        return d.preview ? <span className="italic truncate max-w-[200px] inline-block align-bottom">"{d.preview}"</span> : null
      default:
        return null
    }
  }

  if (isLoading) {
    return <p className="text-xs text-gray-400 py-2">Caricamento...</p>
  }

  if (activities.length === 0) {
    return <p className="text-xs text-gray-400 py-2">Nessuna attività registrata</p>
  }

  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto">
      {activities.map((activity) => {
        const config = ACTION_CONFIG[activity.action] || ACTION_CONFIG.created
        const Icon = config.icon
        return (
          <div key={activity.id} className="flex items-start gap-2 text-xs">
            <div className={`p-1 rounded-full shrink-0 mt-0.5 ${config.color}`}>
              <Icon className="h-2.5 w-2.5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-gray-700">{config.label}</span>
              {renderDetails(activity) && (
                <span className="text-gray-500 ml-1">{renderDetails(activity)}</span>
              )}
            </div>
            <span className="text-gray-400 shrink-0">{formatTime(activity.created_at)}</span>
          </div>
        )
      })}
    </div>
  )
}

